import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../utils/auth';
import { User, Building2, Shield, Bell, Save, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
type Tab = 'profile' | 'company' | 'security' | 'notifications';
export function Settings() {
  const {
    user
  } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [isSaving, setIsSaving] = useState(false);
  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: `${user?.name.toLowerCase().replace(' ', '.')}@ipcom-fr.com`,
    phone: '+33 6 12 34 56 78',
    role: user?.role || 'admin'
  });
  // Company form state
  const [companyData, setCompanyData] = useState({
    name: 'France Décoration',
    address: '123 Avenue des Champs-Élysées',
    city: 'Paris',
    country: 'France',
    siret: '123 456 789 00012'
  });
  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
  };
  const tabs = [{
    id: 'profile' as Tab,
    label: 'Profil',
    icon: User
  }, {
    id: 'company' as Tab,
    label: 'Entreprise',
    icon: Building2
  }, {
    id: 'security' as Tab,
    label: 'Sécurité',
    icon: Shield
  }, {
    id: 'notifications' as Tab,
    label: 'Notifications',
    icon: Bell
  }];
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-784">
      <div className="page-container section-spacing" data-id="element-785">
        <div data-id="element-786">
          <h1 className="heading-xl" data-id="element-787">Paramètres</h1>
          <p className="text-caption mt-1" data-id="element-788">
            Gérer les paramètres de votre compte et de l'entreprise
          </p>
        </div>

        {/* Mobile: Horizontal scrollable tabs */}
        <div className="md:hidden flex gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4" data-id="element-789">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'bg-[#1E2A3A] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-790">
              <tab.icon size={18} data-id="element-791" />
              <span className="font-medium text-sm" data-id="element-792">{tab.label}</span>
            </button>)}
        </div>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-793">
          {/* Desktop: Tabs Sidebar */}
          <div className="hidden md:block md:w-64 space-y-2 flex-shrink-0" data-id="element-794">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${activeTab === tab.id ? 'bg-[#1E2A3A] text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-795">
                <tab.icon size={20} data-id="element-796" />
                <span className="font-medium" data-id="element-797">{tab.label}</span>
              </button>)}
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0" data-id="element-798">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-799">
              {/* Profile Tab */}
              {activeTab === 'profile' && <Card className="p-4 sm:p-6 space-y-6" data-id="element-800">
                  <div data-id="element-801">
                    <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-802">
                      Informations du Profil
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-803">
                      Gérer vos informations personnelles
                    </p>
                  </div>

                  {/* Avatar */}
                  <div className="flex items-center gap-4" data-id="element-804">
                    <img src={user?.avatar} alt={user?.name} className="w-20 h-20 rounded-full ring-4 ring-gray-100" data-id="element-805" />
                    <div data-id="element-806">
                      <Button size="sm" leftIcon={<Camera size={16} data-id="element-808" />} data-id="element-807">
                        Changer la photo
                      </Button>
                      <p className="text-xs text-gray-500 mt-2" data-id="element-809">
                        JPG, PNG. Max 2MB
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-810">
                    <div data-id="element-811">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-812">
                        Nom complet
                      </label>
                      <input type="text" value={profileData.name} onChange={e => setProfileData({
                    ...profileData,
                    name: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-813" />
                    </div>

                    <div data-id="element-814">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-815">
                        Email
                      </label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-816" />
                    </div>

                    <div data-id="element-817">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-818">
                        Téléphone
                      </label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({
                    ...profileData,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-819" />
                    </div>

                    <div data-id="element-820">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-821">
                        Rôle
                      </label>
                      <input type="text" value={profileData.role === 'admin' ? 'Administrateur' : profileData.role} disabled className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" data-id="element-822" />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200" data-id="element-823">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-824">
                      Changer le mot de passe
                    </h3>
                    <div className="space-y-4" data-id="element-825">
                      <div data-id="element-826">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-827">
                          Mot de passe actuel
                        </label>
                        <input type="password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" placeholder="••••••••" data-id="element-828" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-829">
                        <div data-id="element-830">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-831">
                            Nouveau mot de passe
                          </label>
                          <input type="password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" placeholder="••••••••" data-id="element-832" />
                        </div>
                        <div data-id="element-833">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-834">
                            Confirmer le mot de passe
                          </label>
                          <input type="password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" placeholder="••••••••" data-id="element-835" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>}

              {/* Company Tab */}
              {activeTab === 'company' && <Card className="p-4 sm:p-6 space-y-6" data-id="element-836">
                  <div data-id="element-837">
                    <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-838">
                      Informations de l'Entreprise
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-839">
                      Gérer les détails de votre entreprise
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-840">
                    <div data-id="element-841">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-842">
                        Nom de l'entreprise
                      </label>
                      <input type="text" value={companyData.name} onChange={e => setCompanyData({
                    ...companyData,
                    name: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-843" />
                    </div>

                    <div data-id="element-844">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-845">
                        Adresse
                      </label>
                      <input type="text" value={companyData.address} onChange={e => setCompanyData({
                    ...companyData,
                    address: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-846" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-847">
                      <div data-id="element-848">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-849">
                          Ville
                        </label>
                        <input type="text" value={companyData.city} onChange={e => setCompanyData({
                      ...companyData,
                      city: e.target.value
                    })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-850" />
                      </div>
                      <div data-id="element-851">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-852">
                          Pays
                        </label>
                        <input type="text" value={companyData.country} onChange={e => setCompanyData({
                      ...companyData,
                      country: e.target.value
                    })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-853" />
                      </div>
                    </div>

                    <div data-id="element-854">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-855">
                        SIRET
                      </label>
                      <input type="text" value={companyData.siret} onChange={e => setCompanyData({
                    ...companyData,
                    siret: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none" data-id="element-856" />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200" data-id="element-857">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-858">
                      Branding
                    </h3>
                    <div className="flex items-center gap-4" data-id="element-859">
                      <div className="w-20 h-20 bg-[#0F172A] rounded-lg flex items-center justify-center text-white font-bold text-2xl" data-id="element-860">
                        IP
                      </div>
                      <div data-id="element-861">
                        <Button size="sm" variant="outline" data-id="element-862">
                          Changer le logo
                        </Button>
                        <p className="text-xs text-gray-500 mt-2" data-id="element-863">
                          PNG, SVG. Carré recommandé
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>}

              {/* Security Tab */}
              {activeTab === 'security' && <Card className="p-6 space-y-6" data-id="element-864">
                  <div data-id="element-865">
                    <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-866">
                      Sécurité
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-867">
                      Gérer la sécurité de votre compte
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-868">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-869">
                      <div data-id="element-870">
                        <h3 className="font-medium text-gray-900" data-id="element-871">
                          Authentification à deux facteurs
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-872">
                          Ajouter une couche de sécurité supplémentaire
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-873">
                        <input type="checkbox" className="sr-only peer" data-id="element-874" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F59E0B]" data-id="element-875"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-876">
                      <div data-id="element-877">
                        <h3 className="font-medium text-gray-900" data-id="element-878">
                          Sessions actives
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-879">
                          Gérer vos sessions de connexion
                        </p>
                      </div>
                      <Button variant="outline" size="sm" data-id="element-880">
                        Voir les sessions
                      </Button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-881">
                      <div data-id="element-882">
                        <h3 className="font-medium text-gray-900" data-id="element-883">
                          Historique de connexion
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-884">
                          Dernière connexion: Aujourd'hui à 14:32
                        </p>
                      </div>
                      <Button variant="outline" size="sm" data-id="element-885">
                        Voir l'historique
                      </Button>
                    </div>
                  </div>
                </Card>}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && <Card className="p-6 space-y-6" data-id="element-886">
                  <div data-id="element-887">
                    <h2 className="text-lg font-bold text-gray-900 mb-1" data-id="element-888">
                      Notifications
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-889">
                      Gérer vos préférences de notification
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-890">
                    {[{
                  label: 'Nouveaux projets',
                  desc: "Recevoir une notification lors de la création d'un projet"
                }, {
                  label: 'Photos uploadées',
                  desc: 'Notification quand un agent upload des photos'
                }, {
                  label: 'Fin de projet',
                  desc: 'Notification quand un projet est terminé'
                }, {
                  label: "Assignation d'équipe",
                  desc: "Notification lors de l'assignation à un projet"
                }].map((item, i) => <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-891">
                        <div data-id="element-892">
                          <h3 className="font-medium text-gray-900" data-id="element-893">
                            {item.label}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-894">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer" data-id="element-895">
                          <input type="checkbox" className="sr-only peer" defaultChecked data-id="element-896" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F59E0B]" data-id="element-897"></div>
                        </label>
                      </div>)}
                  </div>
                </Card>}

              {/* Save Button */}
              <div className="mt-6 flex justify-end" data-id="element-898">
                <Button onClick={handleSave} isLoading={isSaving} leftIcon={<Save size={18} data-id="element-900" />} className="w-full sm:w-auto" data-id="element-899">
                  Enregistrer les modifications
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}